import React from "react";
import DatePicker from "Components/DesignSystem/DatePicker/DatePicker";
import Button from "Components/DesignSystem/Button/Button";
import Typography from "Components/DesignSystem/Typography/Typography";

/**
 * "Occurs every … until <date>" — the end-date control shared by the Hourly, Daily and
 * Weekly schedule rows.
 *
 * **Extracted because the three were the same 40 lines three times.**
 * `HourRepeatSchedule.js`, `DayRepeatSchedule.js` and `WeekRepeatSchdule.js` carried
 * byte-identical `handleEndDateClick` / `handleEndDateSelection` / `handleClearEndDate`
 * and the same conditional JSX, differing only in the sentence and — in one of them —
 * a font size of `0.8125` instead of `0.875`. Three copies is also why the
 * react-datepicker@9 `disabled` regression (`okf/connector/e2e.md` § 7) had to be
 * fixed in three places, and why the `popperProps` fix after it did too.
 *
 * **The three-state flow is preserved exactly**, because it is what shipped:
 *
 * | state | what renders |
 * |---|---|
 * | no end date, calendar closed | a "Choose end date" button |
 * | calendar open | the date field with its calendar |
 * | end date chosen | the date as text, plus "Select end date" |
 *
 * Including the quirk that "Select end date" **toggles the calendar back open** while
 * clearing the value — it reads as "clear" and behaves as "change", and both the
 * label and the behaviour are left alone here.
 *
 * What is gone is the component injection: the three used to receive `Label`,
 * `GMSBorderlessButton` and `DatePicker1` **as props** from `ScheduleForm`, so the
 * markup a row rendered was decided by its parent's import list. Each control is now
 * imported where it is used.
 *
 * The DS `DatePicker` hands back the `YYYY-MM-DD` string directly, which is why the
 * `formatEndDate` prop is gone too — it was a fourth copy of the same zero-padding
 * formatter, and its comment claimed it produced `dd-mm-yyyy`.
 */
const EndDateRow = ({
  label,
  instanceId,
  scheduleInfo,
  setScheduleInfo,
  openDatePicker,
  setOpenDatePicker,
}) => {
  const handleEndDateClick = () => {
    setOpenDatePicker((prev) => !prev);
  };

  const handleEndDateSelection = (_date, formatted) => {
    setScheduleInfo((prevScheduleInfo) => ({
      ...prevScheduleInfo,
      endDate: formatted,
    }));
    setOpenDatePicker((prev) => !prev);
  };

  const handleClearEndDate = () => {
    setOpenDatePicker((prev) => !prev);
    setScheduleInfo((prevScheduleInfo) => ({
      ...prevScheduleInfo,
      endDate: "",
    }));
  };

  const hasEndDate = scheduleInfo.endDate !== "";

  return (
    <div className="tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-gap-3" data-testid={instanceId}>
      <Typography variant="caption-metadata" as="span" className="tw-text-ink-secondary">
        {label}
      </Typography>

      {!openDatePicker && !hasEndDate && (
        <Button
          variant="tertiary"
          size="sm"
          data-testid={`${instanceId}-choose-btn`}
          onClick={handleEndDateClick}
        >
          Choose end date
        </Button>
      )}

      {hasEndDate && (
        <Typography
          variant="body-primary"
          as="span"
          className="tw-text-accent-bold"
          data-testid={`${instanceId}-value`}
        >
          {scheduleInfo.endDate}
        </Typography>
      )}

      {openDatePicker && (
        <DatePicker
          label=""
          aria-label="End date"
          value={scheduleInfo.endDate}
          onChange={handleEndDateSelection}
          /* The ONLY thing allowed to gate a day. Passing react-datepicker's top-level
             `disabled` alongside a controlled `open` is what killed every day cell on
             gms-dev (§ 7); DS `DatePicker` makes that unreachable rather than relying
             on nobody re-adding it. */
          minDate={new Date()}
          open={openDatePicker}
          size="sm"
          className="tw-w-[150px]"
          data-testid={`${instanceId}-input`}
        />
      )}

      {hasEndDate && (
        <Button
          variant="tertiary"
          size="sm"
          data-testid={`${instanceId}-clear-btn`}
          onClick={handleClearEndDate}
        >
          Select end date
        </Button>
      )}
    </div>
  );
};

export default EndDateRow;
